import { ThemedText } from "@/components/themed-text";
import { BrandColors } from "@/constants/theme";
import { isCustomerRole, isDriverRole } from "@/lib/roles";
import { formatDistance, formatETA } from "@/lib/utils/map-utils";
import Ionicons from "@react-native-vector-icons/ionicons";
import React from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { formatCollectionDate } from "./booking-card-utils";

interface BookingHeaderCardProps {
  serviceTypeLabel: string;
  statusLabel: string;
  status: string;
  paymentMethod?: string;
  distanceTravelled?: {
    duration: number;
    distance: number;
  } | null;
  userRole?: string;
  showNavigation?: boolean;
  onNavigate?: () => void;
  scheduledAt?: string;
}

export const BookingHeaderCard: React.FC<BookingHeaderCardProps> = ({
  scheduledAt,
  serviceTypeLabel,
  statusLabel,
  status,
  paymentMethod,
  distanceTravelled,
  userRole,
  showNavigation,
  onNavigate,
}) => {
  return (
    <View style={styles.sheetHeader}>
      <View style={styles.headerLeft}>
        <View style={styles.titleRow}>
          <ThemedText style={styles.serviceType} type="subtitle">
            {serviceTypeLabel}
          </ThemedText>
          <View
            style={[
              styles.statusBadge,
              {
                backgroundColor:
                  status === "completed"
                    ? "rgba(35, 209, 139, 0.15)"
                    : status === "cancelled" ? "rgba(255, 0, 0, 0.15)" : "rgba(15, 185, 177, 0.15)",
              },
            ]}
          >
            <ThemedText
              style={[
                styles.statusText,
                {
                  color:
                    status === "completed"
                      ? BrandColors.success
                      : status === "cancelled" ? BrandColors.error : BrandColors.primary,
                },
              ]}
              type="extraSmall"
            >
              {statusLabel}
            </ThemedText>
          </View>
        </View>

        {(status === "pending" || status === "quoted") && (
          <ThemedText style={styles.statusSubtext} type="extraSmall">
            Waiting for driver to accept...
          </ThemedText>
        )}

        {(status === "scheduled") && (
          <ThemedText style={styles.statusSubtext} type="extraSmall">
            This job will start at the scheduled
            time {formatCollectionDate(scheduledAt ?? "")}.
          </ThemedText>
        )}

        {isCustomerRole(userRole) &&
          (status === "in_progress" ||
            status === "delivered" ||
            status === "delivery_inspection") && (
            <ThemedText style={styles.statusSubtext} type="extraSmall">
              The operator will ask for a verification code at the time of
              delivery. Please check your email for the code.
            </ThemedText>
          )}

        {/* ETA and Distance Bar */}
        {!!distanceTravelled && (
          <View style={styles.etaBar}>
            <View style={styles.etaItem}>
              <Ionicons name="time" size={14} color={BrandColors.primary} />
              <ThemedText style={styles.etaValue} type="extraSmallSemiBold">
                {formatETA(distanceTravelled?.duration)}
              </ThemedText>
              <ThemedText style={styles.etaLabel} type="extraSmall">
                ETA
              </ThemedText>
            </View>
            <View style={styles.etaDivider} />
            <View style={styles.etaItem}>
              <Ionicons name="navigate" size={14} color={BrandColors.primary} />
              <ThemedText style={styles.etaValue} type="extraSmallSemiBold">
                {formatDistance(distanceTravelled?.distance)}
              </ThemedText>
              <ThemedText style={styles.etaLabel} type="extraSmall">
                Dist
              </ThemedText>
            </View>
          </View>
        )}
      </View>

      <View style={styles.headerRight}>
        <View style={styles.headerActions}>
          {isDriverRole(userRole) && paymentMethod === "cash" && (
            <View style={styles.cashBadge}>
              <Ionicons
                name="cash"
                size={12}
                color="#fff"
                style={{ marginRight: 4 }}
              />
              <ThemedText style={styles.cashBadgeText} type="extraSmall">
                CASH
              </ThemedText>
            </View>
          )}

          <View style={styles.actionGroup}>
            {userRole === "driver" && showNavigation && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={onNavigate}
                activeOpacity={0.7}
              >
                <View
                  style={[styles.headerActionButton, styles.navActionButton]}
                >
                  <Ionicons name="navigate" size={20} color="#fff" />
                </View>
                <ThemedText type="extraSmall">Maps</ThemedText>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  sheetHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: BrandColors.mediumGray + "50",
  },
  headerLeft: {
    flex: 1,
    marginRight: 12,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 4,
  },
  headerRight: {
    alignItems: "flex-end",
  },
  serviceType: {
    fontSize: 20,
    fontWeight: "800",
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  statusText: {
    fontSize: 12,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  statusSubtext: {
    marginBottom: 8,
    fontSize: 12,
  },
  headerActions: {
    alignItems: "flex-end",
    gap: 8,
  },
  actionGroup: {
    flexDirection: "row",
    gap: 8,
  },
  headerActionButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    justifyContent: "center",
    alignItems: "center",
  },
  actionButton: {
    alignItems: "center",
    gap: 4,
  },
  navActionButton: {
    backgroundColor: BrandColors.primary,
  },
  cashBadge: {
    backgroundColor: "#FF9500",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    flexDirection: "row",
    alignItems: "center",
  },
  cashBadgeText: {
    color: "#fff",
    fontWeight: "700",
  },
  etaBar: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: BrandColors.primaryLight,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 12,
    marginTop: 8,
    alignSelf: "flex-start",
  },
  etaItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  etaDivider: {
    width: 1,
    height: 12,
    backgroundColor: BrandColors.lightGray,
    marginHorizontal: 12,
  },
  etaValue: {
    color: BrandColors.charcoal,
  },
  etaLabel: {
    fontSize: 10,
    color: BrandColors.mediumGray,
    marginLeft: 2,
    fontWeight: "600",
  },
});
